/* eslint-disable camelcase */
/* Tela 2 · Emitir NFS-e (UC01) e Tela 3 · Corrigir rejeição (US06): cartões numerados
   à esquerda e, à direita, verificações, resumo e ações. Validação e envio aparecem na
   Dynamic Island. */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Flex, Grid, useDisclosure } from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Code, FilePlus, XCircle } from 'phosphor-react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useNfseEmissao } from '../../../../../hooks/useNfseEmissao';
import { useAuth } from '../../../../../providers/auth';
import {
  INfseDpsForm,
  INfseEmissao,
  INfseEmpresaBusca,
  INfseValidacao,
} from '../../../../../types/nfseEmissao';
import { extractApiErrorCode, extractApiErrorMessage } from '../../../../../utils/ErrorUtils';
import { CertificadoUploadModal } from '../shared/CertificadoUploadModal';
import {
  editarPath,
  ERRO_NEGOCIO,
  NFSE_ACOES,
  NFSE_EMISSAO_PATH,
  NFSE_PATHS,
  notaPath,
} from '../shared/constants';
import { island } from '../shared/NfseIsland';
import { Carregando, Cascata, NfsePage } from '../shared/NfsePage';
import { Banner, EnvBadge, ModuleHeader, NButton } from '../shared/ui';
import { DPS_VAZIA, dpsSchema } from './schema';
import { ComexSection } from './components/ComexSection';
import { EmitenteSection } from './components/EmitenteSection';
import { TomadorSection } from './components/TomadorSection';
import { ServicoSection } from './components/ServicoSection';
import { ValoresSection } from './components/ValoresSection';
import { VerificacoesPanel } from './components/VerificacoesPanel';
import { TodosOsCampos, useXmlDaDps } from './components/DpsExtras';

const EDITAVEL = ['rascunho', 'rejeitada'];

function igual(a: unknown, b: unknown) {
  const norm = (v: unknown) => (v === '' || v === undefined ? null : v);
  return JSON.stringify(norm(a)) === JSON.stringify(norm(b));
}

function Conteudo() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams();
  const emissaoId = id ? Number(id) : null;
  const [searchParams] = useSearchParams();
  const emitenteInicial = Number(searchParams.get('emitente')) || null;
  const duplicarDe = Number(searchParams.get('duplicar')) || null;

  const { hasPermission } = useAuth();
  const temPermissao = hasPermission(NFSE_EMISSAO_PATH, NFSE_ACOES.emitir);
  const api = useNfseEmissao();
  const certModal = useDisclosure();
  const [validacao, setValidacao] = useState<INfseValidacao | null>(null);
  const [empresaPendente, setEmpresaPendente] = useState<INfseEmpresaBusca | null>(null);
  const carregado = useRef<number | null>(null);

  const methods = useForm<INfseDpsForm>({
    resolver: yupResolver(dpsSchema as any),
    defaultValues: { ...DPS_VAZIA, emitente_id: emitenteInicial },
  });
  const xml = useXmlDaDps(methods.getValues);

  const emitentesQuery = useQuery({ queryKey: ['nfse-emitentes'], queryFn: api.getEmitentes });
  const ambiente = useQuery({ queryKey: ['nfse-painel', 'mes'], queryFn: api.getPainel }).data
    ?.ambiente;
  const emissaoQuery = useQuery({
    queryKey: ['nfse-emissao', (emissaoId ?? duplicarDe) as number],
    queryFn: api.getEmissao,
    enabled: !!(emissaoId ?? duplicarDe),
  });
  // Ao duplicar, a nota de origem só empresta os campos: a nova não tem id nem número.
  const emissao = emissaoId ? emissaoQuery.data : undefined;

  const emitenteId = useWatch({ control: methods.control, name: 'emitente_id' });
  const emitentes = emitentesQuery.data ?? [];
  const emitente = emitentes.find(e => e.id === emitenteId);

  const ultimaNotaQuery = useQuery({
    queryKey: ['nfse-ultima-nota', emitenteId as number],
    queryFn: api.getUltimaNota,
    enabled: !!emitenteId,
  });
  const padroesQuery = useQuery({
    queryKey: ['nfse-padroes', emitenteId as number],
    queryFn: api.getPadroes,
    enabled: !!emitenteId,
  });
  const padroes = padroesQuery.data;

  // A emissão salva entra no formulário uma vez por id; depois quem manda é quem digita.
  useEffect(() => {
    const origem = emissaoQuery.data;
    if (!origem?.form || carregado.current === origem.id) return;
    carregado.current = origem.id;
    if (duplicarDe && !emissaoId) {
      methods.reset({ ...DPS_VAZIA, ...origem.form, n_dps: null });
      island.notify('Campos copiados da NFS-e', {
        tone: 'info',
        meta: `nº ${origem.n_nfse ?? origem.n_dps}`,
      });
    } else {
      // Rascunho de antes da TI-2026-0358 não tem dedução nem opção de e-mail.
      methods.reset({ ...DPS_VAZIA, ...origem.form });
    }
  }, [emissaoQuery.data]);

  // Padrões do emitente só preenchem campo vazio, e só em nota nova.
  useEffect(() => {
    if (!padroes || emissaoId || duplicarDe) return;
    const atual = methods.getValues();
    Object.entries(padroes).forEach(([campo, valor]) => {
      // De onde veio o cTribNac (emitente ou histórico) é informação, não campo da DPS.
      if (campo.endsWith('_origem')) return;
      const vazio = atual[campo] === '' || atual[campo] == null;
      if (vazio || campo === 'retencoes')
        methods.setValue(campo as keyof INfseDpsForm, valor as any);
    });
  }, [padroes]);

  // Qualquer edição depois de validar volta a exigir validação.
  useEffect(() => {
    const sub = methods.watch((_, { name }) => {
      if (name) setValidacao(v => (v ? null : v));
    });
    return () => sub.unsubscribe();
  }, [methods]);

  const valores = useWatch({ control: methods.control });
  const editado = useMemo(
    () => (campo: string) =>
      !!padroes && campo in padroes && !igual(valores?.[campo], (padroes as any)[campo]),
    [padroes, valores],
  );

  const travada = !!emissao && !EDITAVEL.includes(emissao.status);
  const rejeitada = emissao?.status === 'rejeitada';
  const bloqueado = !temPermissao || travada || !emitente || emitente.situacao !== 'liberado';
  const numero = emissao?.n_dps ?? ultimaNotaQuery.data?.proximo_n_dps ?? emitente?.proximo_n_dps;
  // Só nota com número reservado tem série própria; até lá vale a atual do emitente.
  const serieReservada = emissao?.n_dps ? emissao.serie : undefined;
  const serie = serieReservada ?? emitente?.serie;

  const invalidar = () => {
    [
      'nfse-painel',
      'nfse-emitentes',
      'nfse-emissoes',
      'nfse-contagem',
      'nfse-totalizadores',
      'nfse-ultima-nota',
      'nfse-servicos',
    ].forEach(k => queryClient.invalidateQueries({ queryKey: [k] }));
  };

  // Cria o rascunho na primeira vez e depois só atualiza.
  const salvar = async (extra?: Partial<INfseDpsForm>): Promise<INfseEmissao> => {
    const payload = { ...methods.getValues(), ...extra };
    const salva = emissaoId
      ? await api.updateEmissao(emissaoId, payload)
      : await api.createEmissao(payload);
    carregado.current = salva.id;
    queryClient.setQueryData(['nfse-emissao', salva.id], salva);
    if (!emissaoId) navigate(editarPath(salva.id), { replace: true });
    return salva;
  };

  const tratarErro = (err: any, fallback: string) => {
    if (extractApiErrorCode(err) === ERRO_NEGOCIO.numeroJaEmitido) {
      const detalhe = err?.response?.data?.detail;
      if (detalhe?.validacao) setValidacao(detalhe.validacao);
    }
    island.notify(extractApiErrorMessage(err, fallback), { tone: 'bad', ms: 6000 });
  };

  const salvarMutation = useMutation({
    mutationFn: () => salvar(),
    onSuccess: () => {
      island.notify('Rascunho salvo', { tone: 'ok', meta: 'DPS sem número até o envio' });
      invalidar();
    },
    onError: err => tratarErro(err, 'Não foi possível salvar o rascunho.'),
  });

  const validarMutation = useMutation({
    mutationFn: async () => {
      island.notify(`Validando DPS ${serie} · nº ${numero}`, {
        tone: 'run',
        meta: 'RN14–16 · XSD',
        sticky: true,
      });
      const salva = await salvar();
      return api.validarEmissao(salva.id);
    },
    onSuccess: res => {
      setValidacao(res);
      const falhas = res.verificacoes.filter(v => !v.ok).length;
      island.notify(res.ok ? 'DPS pronta para enviar' : `${falhas} verificação(ões) com problema`, {
        tone: res.ok ? 'ok' : 'bad',
        meta: res.ok ? `${res.verificacoes.length}/${res.verificacoes.length} ok` : 'veja ao lado',
      });
    },
    onError: err => tratarErro(err, 'Não foi possível validar.'),
  });

  const enviarMutation = useMutation({
    mutationFn: async () => {
      const inicio = performance.now();
      island.live(
        'Enviando à Sefin Nacional',
        `DPS ${serie} · nº ${numero} · ${emitente?.razao_social ?? ''} · mTLS`,
      );
      const salva = await salvar();
      const res = await api.enviarEmissao(salva.id);
      return { res, segundos: (performance.now() - inicio) / 1000 };
    },
    onSuccess: ({ res, segundos }) => {
      invalidar();
      queryClient.setQueryData(['nfse-emissao', res.id], res);
      const tempo = `${segundos.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} s`;
      if (res.status === 'autorizada') {
        // RF09: o e-mail automático sai na mesma requisição; falha não muda a nota.
        const email = res.email_automatico;
        if (email && !email.enviado) {
          island.notify(`NFS-e nº ${res.n_nfse} autorizada, mas o e-mail falhou`, {
            tone: 'warn',
            meta: email.erro ?? 'reenvie pela nota',
            ms: 7000,
          });
        } else {
          island.notify(`NFS-e nº ${res.n_nfse} autorizada`, {
            tone: 'ok',
            meta: email?.enviado ? `${tempo} · e-mail enviado para ${email.para}` : tempo,
            ms: email?.enviado ? 5200 : 4200,
          });
        }
        navigate(notaPath(res.id));
      } else {
        setValidacao(null);
        island.notify(`Sefin rejeitou · ${res.erros?.[0]?.codigo ?? ''}`.trim(), {
          tone: 'bad',
          meta: 'veja o motivo no topo',
          ms: 6000,
        });
      }
    },
    onError: err => tratarErro(err, 'Não foi possível emitir a nota.'),
  });

  const usarProximoLivreMutation = useMutation({
    mutationFn: (n: number) => salvar({ n_dps: n }),
    onSuccess: salva => {
      setValidacao(null);
      island.notify(`A nota vai usar o nº ${salva.n_dps}`, {
        tone: 'info',
        meta: 'valide de novo',
      });
    },
    onError: err => tratarErro(err, 'Não foi possível trocar o número.'),
  });

  const comFormValido = (fn: () => void) =>
    methods.handleSubmit(fn, () => island.notify('Confira os campos destacados', { tone: 'warn' }));

  if (emissaoId && emissaoQuery.isLoading) return <Carregando />;

  const erro = rejeitada ? emissao?.erros?.[0] : undefined;
  let titulo = 'Nova NFS-e';
  if (rejeitada) titulo = `Corrigir DPS ${emissao?.serie} · nº ${emissao?.n_dps}`;
  else if (emissao)
    titulo =
      emissao.status === 'rascunho'
        ? 'Rascunho de NFS-e'
        : `DPS ${emissao.serie} · nº ${emissao.n_dps}`;

  return (
    <FormProvider {...methods}>
      <ModuleHeader
        title={titulo}
        icon={FilePlus}
        crumbs={[
          { label: 'Fiscal', to: '/modulesHome/supervisor' },
          { label: 'Emissão de NFS-e', to: NFSE_PATHS.painel },
          { label: rejeitada ? 'Corrigir rejeição' : 'Emitir NFS-e' },
        ]}
        actions={
          <>
            <EnvBadge ambiente={ambiente} />
            <NButton variant="ghost" icon={Code} isDisabled={!emitente} onClick={xml.abrir}>
              Ver XML da DPS
            </NButton>
          </>
        }
      />

      {rejeitada && (
        <Banner tone="danger" icon={XCircle}>
          <b>A Sefin rejeitou esta DPS{erro ? ` · código ${erro.codigo}` : ''}.</b>{' '}
          {erro?.descricao}
          {erro?.complemento ? ` (${erro.complemento})` : ''}
          {emissao?.erros && emissao.erros.length > 1
            ? ` E mais ${emissao.erros.length - 1} apontamento(s).`
            : ''}
          <br />O número {emissao?.n_dps} continua reservado para esta nota: corrija e reenvie, a
          sequência não pula.
        </Banner>
      )}
      {travada && (
        <Banner tone="info">
          Esta nota já saiu ({emissao?.status}). Para mudar dados, use Substituir na página da nota.
        </Banner>
      )}

      <Grid
        templateColumns={{ base: '1fr', xl: 'minmax(0, 1fr) 340px' }}
        gap="18px"
        alignItems="start"
      >
        <Flex direction="column" gap="18px" minW={0}>
          <Cascata i={0}>
            <EmitenteSection
              emitentes={emitentes}
              emitente={emitente}
              ultimaNota={ultimaNotaQuery.data}
              numeroReservado={emissao?.n_dps}
              serieReservada={serieReservada}
              travado={!!emissao?.n_dps || travada}
              podeAlterarNumeracao={temPermissao}
              onNumeracaoAlterada={() => setValidacao(null)}
              empresaPendente={empresaPendente}
              onEmpresaPendente={setEmpresaPendente}
              onEnviarCertificado={certModal.onOpen}
            />
          </Cascata>
          <fieldset
            disabled={bloqueado}
            style={{
              border: 0,
              margin: 0,
              padding: 0,
              minWidth: 0,
              opacity: bloqueado && !travada ? 0.55 : 1,
            }}
          >
            <Flex direction="column" gap="18px">
              <Cascata i={1}>
                <TomadorSection isDisabled={bloqueado} />
              </Cascata>
              <Cascata i={2}>
                <ServicoSection isDisabled={bloqueado} editado={editado} emitenteId={emitenteId} />
              </Cascata>
              <Cascata i={3}>
                <ValoresSection
                  isDisabled={bloqueado}
                  editado={editado}
                  municipio={emitente?.municipio}
                />
              </Cascata>
              <ComexSection isDisabled={bloqueado} />
              <Cascata i={4}>
                <TodosOsCampos
                  emitente={emitente}
                  serie={serie}
                  numero={numero}
                  reservado={!!emissao?.n_dps}
                  ambiente={ambiente}
                />
              </Cascata>
            </Flex>
          </fieldset>
        </Flex>

        <VerificacoesPanel
          emitente={emitente}
          validacao={validacao}
          errosSefin={rejeitada ? emissao?.erros : null}
          numeroDps={numero}
          serie={serie}
          bloqueado={bloqueado}
          reenvio={rejeitada}
          salvando={salvarMutation.isPending || usarProximoLivreMutation.isPending}
          validando={validarMutation.isPending}
          enviando={enviarMutation.isPending}
          onSalvar={() => salvarMutation.mutate()}
          onValidar={comFormValido(() => validarMutation.mutate())}
          onEmitir={comFormValido(() => enviarMutation.mutate())}
          onUsarProximoLivre={n => usarProximoLivreMutation.mutate(n)}
        />
      </Grid>

      {xml.drawer}

      <CertificadoUploadModal
        isOpen={certModal.isOpen}
        onClose={certModal.onClose}
        emitentes={emitentes}
        emitenteId={empresaPendente ? null : emitente?.id}
        empresaInicial={empresaPendente}
        onEnviado={novoEmitenteId => {
          setEmpresaPendente(null);
          methods.setValue('emitente_id', novoEmitenteId, { shouldValidate: true });
        }}
      />
    </FormProvider>
  );
}

export function NfseEmissaoFormPage() {
  const ambiente = useQuery({
    queryKey: ['nfse-painel', 'mes'],
    queryFn: useNfseEmissao().getPainel,
  }).data?.ambiente;
  return (
    <NfsePage wide ambiente={ambiente}>
      <Conteudo />
    </NfsePage>
  );
}
